/**
 * Persistência de rascunhos de confirmação de entrega em IndexedDB.
 *
 * Motivação: o motorista preenche a modal "Confirmar Entrega" (contato + fotos) e,
 * se o token tiver expirado (celular em repouso durante a navegação no Waze), a
 * submissão pode falhar com 401 e a tela é recarregada para re-login — perdendo
 * todos os dados digitados e as fotos. Para que nada seja perdido, gravamos um
 * rascunho ANTES de submeter; se a submissão der certo, o rascunho é apagado; se
 * o app recarregar, o rascunho é re-hidratado na volta para a tela de entregas.
 *
 * As fotos já vêm comprimidas (≤0,5 MB JPEG) e são armazenadas como Blob, suportado
 * nativamente pelo IndexedDB.
 */

export interface DeliveryDraft {
  routeId: number;
  stopId: number;
  delivery_contact: string;
  delivery_contact_document?: string;
  photo1?: Blob | null;
  photo2?: Blob | null;
  /** epoch em milissegundos de quando o rascunho foi salvo */
  savedAt: number;
}

const DB_NAME = 'erp-delivery-drafts';
const DB_VERSION = 1;
const STORE_NAME = 'drafts';

/** Rascunhos mais antigos que isto são considerados obsoletos e descartados. */
export const DELIVERY_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

// ─── Helpers puros (testáveis sem IndexedDB) ─────────────────────────────────

/** Chave composta usada como keyPath no object store. */
export function deliveryDraftKey(routeId: number, stopId: number): string {
  return `${routeId}:${stopId}`;
}

/** Indica se o rascunho está obsoleto em relação a `now`. */
export function isDraftStale(
  draft: Pick<DeliveryDraft, 'savedAt'>,
  now: number,
  maxAgeMs: number = DELIVERY_DRAFT_MAX_AGE_MS,
): boolean {
  return now - draft.savedAt > maxAgeMs;
}

/** Monta um DeliveryDraft a partir dos campos da modal, carimbando o horário. */
export function buildDeliveryDraft(
  routeId: number,
  stopId: number,
  data: {
    delivery_contact: string;
    delivery_contact_document?: string;
    photo1?: Blob | null;
    photo2?: Blob | null;
  },
  savedAt: number,
): DeliveryDraft {
  return {
    routeId,
    stopId,
    delivery_contact: data.delivery_contact,
    delivery_contact_document: data.delivery_contact_document,
    photo1: data.photo1 ?? null,
    photo2: data.photo2 ?? null,
    savedAt,
  };
}

// ─── I/O IndexedDB ───────────────────────────────────────────────────────────

function isIndexedDbAvailable(): boolean {
  return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

interface StoredDraft extends DeliveryDraft {
  key: string;
}

/** Salva (ou substitui) o rascunho da parada. Falha de IndexedDB é silenciada. */
export async function saveDeliveryDraft(draft: DeliveryDraft): Promise<void> {
  if (!isIndexedDbAvailable()) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const record: StoredDraft = { ...draft, key: deliveryDraftKey(draft.routeId, draft.stopId) };
      tx.objectStore(STORE_NAME).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (err) {
    console.warn('Falha ao salvar rascunho de entrega:', err);
  }
}

/** Remove o rascunho da parada. */
export async function deleteDeliveryDraft(routeId: number, stopId: number): Promise<void> {
  if (!isIndexedDbAvailable()) return;
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(deliveryDraftKey(routeId, stopId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (err) {
    console.warn('Falha ao remover rascunho de entrega:', err);
  }
}

/** Lê todos os rascunhos persistidos. */
export async function getAllDeliveryDrafts(): Promise<DeliveryDraft[]> {
  if (!isIndexedDbAvailable()) return [];
  try {
    const db = await openDb();
    const drafts = await new Promise<DeliveryDraft[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).getAll();
      request.onsuccess = () => resolve((request.result as StoredDraft[]).map(stripKey));
      request.onerror = () => reject(request.error);
    });
    db.close();
    return drafts;
  } catch (err) {
    console.warn('Falha ao ler rascunhos de entrega:', err);
    return [];
  }
}

/**
 * Retorna o rascunho não-obsoleto de uma rota (se houver), apagando de passagem
 * quaisquer rascunhos obsoletos. Usado para re-hidratar a modal após um reload.
 */
export async function getActiveDraftForRoute(routeId: number, now: number): Promise<DeliveryDraft | null> {
  const all = await getAllDeliveryDrafts();
  let active: DeliveryDraft | null = null;
  for (const draft of all) {
    if (isDraftStale(draft, now)) {
      await deleteDeliveryDraft(draft.routeId, draft.stopId);
      continue;
    }
    if (draft.routeId === routeId && !active) {
      active = draft;
    }
  }
  return active;
}

function stripKey(record: StoredDraft): DeliveryDraft {
  const { key: _key, ...rest } = record;
  return rest;
}
